import _ from 'lodash';
import { go, chan, put, putAsync } from 'js-csp';

import ReconnectingWebsocket from 'assets/js/api/wsreconnect';

// SpawnWSHandler spawns a websocket thread, main channel for websocket
// notifications, and logs in and does websocket authentication. It should
// be initialized on startup of the SPA.
// TODO Use objects instead of arrays for faster and less buggy lookup.
export const SpawnWSHandler = (url, toaster, store) => {
  const ws = new ReconnectingWebsocket(url);
  if (_.isUndefined(ws)) throw Error('Websocket failed to init');
  ws.reconnectInterval = 4000;
  ws.maxReconnectInterval = 60000;
  ws.reconnectDecay = 2;
  ws.timeoutInterval = 5000;
  ws.debug = process.env.NODE_ENV === 'development';

  // Subscribers are in the form:
  //  {
  //    name: String,
  //    channel: js-csp channel,
  //    filter: Function,
  //  }
  // The channel downstream receives the filtered messages as determined by
  // the filter function. The filter function returns true if the message is
  // pertinent to the subscriber, and otherwise returns false.
  const subscribers = [];

  const mainCh = chan();

  // Main channel handler.
  try {
    go(function* chHandler() {
      for (;;) {
        const value = yield mainCh;
        switch (value) {
          case 'fail':
          case 'kill': {
            ws.onclose = () => {};
            if (subscribers.length > 0) {
              for (let i = 0; i < subscribers.length; i += 1) {
                if (!subscribers[i].killswitch.isClosed()) {
                  yield put(subscribers[i].killswitch, 'kill');
                }
              }
            }
            ws.close();
            mainCh.close();

            if (value === 'fail') {
              store.dispatch('SetWsFailure', true);
              /* Commented out by Brandon. See Issue #5427.
              toaster('User token has failed validation, please log back ' +
                'in to re-enable notifications', 'danger', 99999999999);
              */
            }
            return;
          }
          // Pong the pings.
          case 'ping': {
            ws.send('pong');
            break;
          }
          // We've logged in successfully.
          case 'authsuccess': {
            ws.send('yay!');
            if (subscribers.length > 0) {
              for (let i = 0; i < subscribers.length; i += 1) {
                if (!subscribers[i].channel.isClosed()) {
                  yield put(subscribers[i].channel, 'login');
                }
              }
            }
            break;
          }
          // The channel has died for some reason and is being reconnected.
          // Let the channel subscribers know so they can wait for it to
          // reopen and resubscribe.
          case 'close': {
            if (subscribers.length > 0) {
              for (let i = 0; i < subscribers.length; i += 1) {
                if (!subscribers[i].channel.isClosed()) {
                  yield put(subscribers[i].channel, 'close');
                }
              }
            }
            break;
          }
          default: {
            if (!value) break;

            // Is this a message instructing us to add or delete a
            // subscriber?
            if (value.wsSubscribe) {
              // Handle removing subscriber.
              if (value.delete) {
                const idx = _.findIndex(
                  subscribers,
                  (subscr) => subscr.name === value.name,
                );
                if (idx === -1) {
                  throw Error(`can not find subscr ${value.name} to rm`);
                } else {
                  subscribers.splice(idx, 1);
                }
              } else {
                // Handle adding subscriber.
                subscribers.push({
                  name: value.name,
                  channel: value.channel,
                  killswitch: value.killswitch,
                  filter: value.filter,
                });
              }

              break;
            }

            // Handle higher level messages.
            const msg = JSON.parse(value);

            // Otherwise, handle the actual messages by finding a subscriber
            // for them and pushing them to the subscriber.
            if (subscribers.length > 0) {
              for (let i = 0; i < subscribers.length; i += 1) {
                if (subscribers[i].filter(msg)) {
                  if (!subscribers[i].channel.isClosed()) {
                    yield put(subscribers[i].channel, msg);
                  }
                }
              }
            }

            break;
          }
        }
      }
    });
  } catch (err) {
    console.warn(`uncaught exception in main websocket channel: ${err}`);
  }

  // Start handling messages as soon as we're connected.
  ws.onmessage = (event) => {
    // The channel was killed somehow; kill the websocket.
    if (mainCh.isClosed()) {
      ws.close();
      return;
    }
    const data = _.get(event, 'data', null);
    if (_.isNull(data)) {
      console.warn('an empty message event was passed to the main websocket' +
        'channel; aborting put');
      return;
    }
    putAsync(mainCh, data);
  };

  // Pass the jwt and auth us.
  ws.onopen = () => {
    ws.send(store.state.auth.jwt);
  };

  // Let the main channel know if there's been a closure.
  ws.onclose = () => {
    if (mainCh.isClosed()) {
      return;
    }
    putAsync(mainCh, 'close');
  };

  ws.onerror = () => {
    console.warn('The websocket has encountered an error, such as a ' +
      'connectivity failure. It will attempt to retry your connection ' +
      'after a short period of time.');
  };

  // Debug mode for development: return an object that we can manipulate and
  // interact with instead of just the channel.
  if (ws.debug) {
    return {
      mainCh,
      put: (val) => putAsync(mainCh, val),
      websocket: ws,
    };
  }

  return {
    mainCh,
    websocket: ws,
  };
};

// Subscriber creates a subscriber with a channel listening for events.
// It requires the following arguments:
//
//   name: A unique name used to identify the channel.
//   mainCh: The main channel created by SpawnWSHandler that handles all
//     information delivered through the websocket.
//   filter: function that returns true if the message received is for this
//     subscriber, else false. Takes a single argument, the partially decoded
//     JSON message.
//   onCreate: function to be run on creation (generally, one that subscribes
//     the subscriber to the main channel). (optional)
//   onOpen: function to be run on reconnect (generally, one that subscribes
//     the subscriber to the main channel). (optional)
//   onMsg: function that runs when we receive a relevant message. Takes a
//     single argument, the partially decoded JSON message. (optional)
//   onClose: function to be run on close (e.g. push a message to the
//     subscriber's channel about there being a disconnection). (optional)
//   onKill: function to be run when the subscriber is killed (generally,
//     one that unsubscribes the subscriber from the main channel). (optional)
//
// It returns the following channel:
//   killswitch: A js-csp channel which kills the process upon receiving
//     any signal. The caller can use putAsync to deliver a kill message to
//     this channel.
// }
export const Subscriber = (
  name, mainCh, filter, onCreate, onOpen, onMsg,
  onClose, onKill,
) => {
  // Init.
  let onCreateLocal = () => {};
  if (onCreate) onCreateLocal = onCreate;
  let onOpenLocal = () => {};
  if (onOpen) onOpenLocal = onOpen;
  let onMsgLocal = () => {};
  if (onMsg) onMsgLocal = onMsg;
  let onCloseLocal = () => {};
  if (onClose) onCloseLocal = onClose;
  let onKillLocal = () => {};
  if (onKill) onKillLocal = onKill;

  onCreateLocal();

  const subCh = chan();
  const killswitch = chan();

  // Add the subscriber in the main channel.
  if (!mainCh) throw Error('undefined main channel to add subscriber to');
  if (mainCh.isClosed()) {
    throw Error('main channel is closed; the websocket is probably not ' +
    'connected');
  }
  putAsync(mainCh, {
    wsSubscribe: true,
    name,
    channel: subCh,
    killswitch,
    filter,
  });

  // Goroutine handling the killswitch.
  go(function* chHandler() {
    yield killswitch;
    onKillLocal();
    if (!subCh.isClosed()) yield put(subCh, 'kill');
    killswitch.close();
  });

  // Goroutine handling messages from the main channel.
  try {
    go(function* chHandler() {
      for (;;) {
        const value = yield subCh;
        switch (value) {
          case 'kill': {
            // Drop the subscriber from the main channel.
            if (!mainCh.isClosed()) {
              yield put(mainCh, {
                wsSubscribe: true,
                delete: true,
                name,
              });
            }
            subCh.close();
            return;
          }
          // We've logged in successfully.
          case 'login': {
            onOpenLocal();
            break;
          }
          case 'close': {
            onCloseLocal();
            break;
          }
          // Should be a JSON message of interest at this point.
          // Pass it into our channel.
          default: {
            onMsgLocal(value);
            break;
          }
        }
      }
    });
  } catch (err) {
    console.warn(`uncaught exception in subscriber websocket channel ${name}:` +
      ` ${err}`);
  }

  return killswitch;
};
